/**
 * Shared UI design tokens (TPWR-127).
 *
 * The canonical source for non-art interface presentation: the interface color palette,
 * the type scale, and spacing. The palette uses neutral charcoal, weathered brass, and
 * parchment values so interface chrome supports the painted world art instead of adding
 * a competing green/black prototype palette.
 *
 * Scope boundary: this owns colors that carry no simulation meaning. Gameplay and FX
 * palettes — terrain, tile, attack, enemy-status colors — stay in their own modules, where
 * the color IS data. Scene-specific accents that no other scene shares (e.g. MenuScene's
 * card greens) also stay local; a token exists to remove *duplication of equivalent
 * controls*, not to centralize every literal.
 *
 * Numeric values are Phaser tints (for GameObject `.setTint` / rects); string values are
 * CSS hex for text-style `color`. Use `cssHex()` to bridge a tint into a text color.
 */

/** Interface font stack. Painted-pixel HUD text uses a clean sans, not a pixel font. */
export const fontFamily = 'Inter, Arial, Helvetica, sans-serif';

/** Type scale, in the px strings Phaser text styles expect. */
export const fontSize = {
  chip: '11px',
  small: '14px',
  body: '16px',
  ui: '18px',
  subheading: '20px',
  heading: '28px',
} as const;

/**
 * Font family slots. Every menu text role resolves its family here, so choosing the art-direction
 * typefaces is a change to these three values and nothing else.
 */
export const fontFamilies = {
  display: '"Barlow Condensed", "Arial Narrow", Arial, sans-serif',
  ui: 'Barlow, Arial, Helvetica, sans-serif',
  numeric: '"Barlow Condensed", "Arial Narrow", Arial, sans-serif',
} as const;

export type FontFamilySlot = keyof typeof fontFamilies;
export type FontWeight = 'normal' | 'bold';

export interface TypeRole {
  family: FontFamilySlot;
  size: string;
  weight: FontWeight;
  letterSpacing?: number;
  lineSpacing?: number;
}

/** Semantic text roles consumed by `uiText`. Sizes come from the `fontSize` scale where one fits. */
export const typeRoles = {
  display: { family: 'display', size: '64px', weight: 'bold' },
  title: { family: 'display', size: '36px', weight: 'bold' },
  heading: { family: 'ui', size: '22px', weight: 'bold', letterSpacing: 1 },
  body: { family: 'ui', size: fontSize.body, weight: 'normal' },
  label: { family: 'ui', size: fontSize.small, weight: 'bold', letterSpacing: 1 },
  caption: { family: 'ui', size: fontSize.small, weight: 'normal' },
  numeric: { family: 'numeric', size: '28px', weight: 'bold' },
} as const satisfies Record<string, TypeRole>;

export type TypeRoleName = keyof typeof typeRoles;

/** Surface fills (Phaser tints). */
export const surface = {
  /** App and scene background. Matches index.html and phaser.config backgroundColor. */
  deep: 0x121310,
  /** Header band above content. */
  mid: 0x1b1c19,
  /** Raised content card. */
  card: 0x20221f,
  /** Campaign control/panel fill (buttons, panels). */
  panel: 0x292b27,
  /** Campaign control fill on hover. */
  panelHover: 0x363832,
  /** Campaign control fill while pressed. */
  panelPressed: 0x232521,
  /** Warm metal surface reserved for the single primary action. */
  accentPanel: 0x403923,
  accentPanelHover: 0x52482c,
  accentPanelPressed: 0x332e20,
} as const;

/** Border tints for interface controls. */
export const border = {
  base: 0x5d5f57,
  strong: 0x8f8a74,
} as const;

/** Interface accent (not a simulation color). */
export const accent = {
  gold: 0xb59b5b,
} as const;

/** Text fill colors, as CSS hex for Phaser text-style `color`. */
export const textColor = {
  primary: '#ece9de',
  muted: '#aaa99d',
} as const;

/** Cool-tinted text for the combat tower palette, readable over the dark forest art. */
export const paletteText = {
  cool: '#d2e3f1',
  dragHint: '#e8f4ff',
  dragHintBackground: '#0b1620',
  labelBackground: 'rgba(11, 22, 32, 0.7)',
} as const;

/**
 * Menu art direction: "Operator Terminal, lantern amber" (docs/ART_STYLE_BIBLE.md, Menu UI).
 * Numeric values are Phaser tints; strings are CSS hex for text color.
 */
export const menuTheme = {
  glass: 0x100d0a,
  glassAlpha: 0.82,
  amber: 0xf0b44c,
  amberHover: 0xf6c66a,
  amberPressed: 0xd99d36,
  hairline: 0x4a4238,
  danger: 0xe0614f,
  success: 0x6fc28a,
  text: {
    /** Amber as label text (active tab); numeric twin is `menuTheme.amber`. */
    amber: '#f0b44c',
    amberInk: '#2a1a04',
    paper: '#fff8ec',
    mist: '#bdb3a3',
  },
} as const;

/** Formats a Phaser tint as a CSS hex string, e.g. 0x0f120d -> "#0f120d". */
export const cssHex = (tint: number): string => `#${tint.toString(16).padStart(6, '0')}`;

/** CSS tokens for the small set of native DOM controls layered over Phaser scenes. */
export const domControl = {
  field: {
    background: cssHex(menuTheme.glass),
    border: cssHex(menuTheme.hairline),
    text: menuTheme.text.paper,
    invalidBorder: cssHex(menuTheme.danger),
  },
  primaryAction: {
    background: cssHex(menuTheme.amber),
    hoverBackground: cssHex(menuTheme.amberHover),
    /** Disabled reads through the button's own opacity, so the fill stays amber. */
    disabledBackground: cssHex(menuTheme.amber),
    text: menuTheme.text.amberInk,
  },
  providerAction: {
    background: cssHex(menuTheme.glass),
    hoverBackground: cssHex(menuTheme.glass),
    text: menuTheme.text.paper,
  },
  link: menuTheme.text.mist,
  divider: cssHex(menuTheme.hairline),
  muted: menuTheme.text.mist,
  focusRing: 'rgba(240, 180, 76, 0.6)',
  focusBorder: cssHex(menuTheme.amber),
  radius: '4px',
  controlPadding: '12px',
  transition: '0.2s',
} as const;

/** Spacing step: 4px grid. `space(2)` === 8. */
export const space = (steps: number): number => steps * 4;
